// Venue-configurable wording for the paid-membership CTA (the tier
// Subscribe button). Default "Subscribe". Love Inn 2026-09-26 set theirs
// to "Support" (they market membership as "supporters" of the space).
//
// Kept as a pure lib so the labels can be pinned in unit tests without
// rendering React; venue.tsx + tiers.tsx both read from here.

export type SubscribeCtaLabels = {
  /** The main CTA button ("Subscribe"). */
  subscribe: string;
  /** In-progress state on the CTA ("Subscribing…" / "Supporting…"). */
  submitting: string;
  /** The signed-out variant that links to /sign-in ("Sign in to subscribe"). */
  signInTo: string;
};

const DEFAULT_LABEL = "Subscribe";

/** Verb form of the label ("Subscribe" -> "subscribe"). Preserves internal
 * casing so a multi-word label like "Become a supporter" still reads well. */
function toVerb(label: string): string {
  if (!label) return DEFAULT_LABEL.toLowerCase();
  return label.charAt(0).toLowerCase() + label.slice(1);
}

/** "Subscribe" -> "Subscribing…". Falls back to "Loading…" for labels
 * where the -ing form is awkward (any label not ending in "e" or a
 * consonant that doubles cleanly). */
function toSubmitting(label: string): string {
  const trimmed = label.trim();
  if (!trimmed) return "Loading…";
  // "Subscribe" -> "Subscribing", "Support" -> "Supporting", "Join" -> "Joining"
  if (/[aeiou]/i.test(trimmed.slice(-1))) {
    return trimmed.replace(/e$/i, "") + "ing…";
  }
  return trimmed + "ing…";
}

/** Just the display word (trim + fallback). Extracted so the design preview
 *  in admin-branding can share the same resolution as the live buttons. */
export function resolveSubscribeCtaLabel(raw: string | null | undefined): string {
  const trimmed = raw?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : DEFAULT_LABEL;
}

export function subscribeCtaLabels(
  appConfig: { subscribeCtaLabel?: string | null } | null | undefined,
): SubscribeCtaLabels {
  const subscribe = resolveSubscribeCtaLabel(appConfig?.subscribeCtaLabel);
  return {
    subscribe,
    submitting: toSubmitting(subscribe),
    signInTo: `Sign in to ${toVerb(subscribe)}`,
  };
}
